import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { uploadRefusalOf, completionRefused } from '@/lib/uploadRefusal';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Upload, X, FileText, Check, Loader2 } from 'lucide-react';

// Large-file multipart thresholds — mirror VideoReview.js exactly.
// Files <= MULTIPART_THRESHOLD continue to use the existing single-PUT path
// (no behavior change for brand assets, small project assets, photo files, etc.).
// Files > MULTIPART_THRESHOLD use the chunked multipart upload path that has
// been in production via VideoReview.js for 3+ months. This is the same R2
// multipart API; no backend change.
const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100 MB
const CHUNK_SIZE = 50 * 1024 * 1024; // 50 MB per part

/**
 * Reusable drag-and-drop file upload zone → Cloudflare R2.
 * @param {string} scope — brand_asset | project_media | project_deliverable | project_asset
 * @param {string} projectId — optional, for project-scoped uploads
 * @param {string} brand — optional, for brand-scoped uploads
 * @param {string} version — optional, for versioned uploads
 * @param {boolean} multiple — allow multiple files
 * @param {string} accept — file type filter (e.g., "video/*", ".xmp,.lrtemplate", "image/*")
 * @param {string} label — display label
 * @param {string} hint — helper text
 * @param {function} onUploadComplete — callback with uploaded file records
 */
export default function FileUploadZone({ scope = 'brand_asset', projectId, brand, version, multiple = false, accept, label, hint, onUploadComplete, icon: IconComp }) {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const [progress, setProgress] = useState('');
  // Byte-level progress for the multipart branch only. Small-file single-PUT
  // path does NOT update this — strict isolation per the original fix.
  const [byteProgress, setByteProgress] = useState({ loaded: 0, total: 0 });
  const inputRef = useRef(null);

  const Icon = IconComp || Upload;

  const handleFiles = (newFiles) => {
    if (multiple) setFiles(prev => [...prev, ...newFiles]);
    else setFiles(newFiles.slice(0, 1));
  };

  const onDragOver = (e) => { e.preventDefault(); e.currentTarget.classList.add('border-gold-leaf/50', 'bg-gold-leaf/[0.04]'); };
  const onDragLeave = (e) => { e.currentTarget.classList.remove('border-gold-leaf/50', 'bg-gold-leaf/[0.04]'); };
  const onDrop = (e) => {
    e.preventDefault(); e.currentTarget.classList.remove('border-gold-leaf/50', 'bg-gold-leaf/[0.04]');
    handleFiles(Array.from(e.dataTransfer.files));
  };
  const onClick = () => inputRef.current?.click();
  const onInputChange = (e) => handleFiles(Array.from(e.target.files));
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    const results = [];
    let currentMp = null; // {file_id, upload_id} of the in-flight multipart, for abort-on-failure
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProgress(`Uploading ${i + 1}/${files.length}...`);
        // Ask what survived a previous attempt before starting a new one. Same
        // reasoning as VideoReview: a failed part used to discard the whole
        // transfer, so a large upload on a slow line could never finish.
        let resume = null;
        if (f.size > MULTIPART_THRESHOLD) {
          try {
            const r = await api.post('/storage/resume-multipart', {
              filename: f.name, scope, project_id: projectId || '',
              version: version || '', size: f.size });
            if (r.data?.resumable && (r.data.parts || []).length) resume = r.data;
          } catch { /* resume is an optimisation; a fresh upload is always valid */ }
        }

        const presign = resume
          ? { data: { file_id: resume.file_id, upload_url: 'RESUMED', key: resume.key } }
          : await api.post('/storage/presign-upload', {
              filename: f.name, scope, project_id: projectId || '', brand: brand || '',
              version: version || '', size: f.size,
              content_type: f.type || 'application/octet-stream',
            });
        const isDirectR2 = presign.data.upload_url && presign.data.upload_url !== 'LOCAL';

        if (isDirectR2 && f.size > MULTIPART_THRESHOLD) {
          // ── LARGE-FILE PATH: chunked multipart to R2 ──
          setByteProgress({ loaded: 0, total: f.size });
          let completedBytes = 0;
          const { data: mp } = resume
            ? { data: { upload_id: resume.upload_id } }
            : await api.post('/storage/initiate-multipart', { file_id: presign.data.file_id });
          currentMp = { file_id: presign.data.file_id, upload_id: mp.upload_id };
          const totalParts = Math.ceil(f.size / CHUNK_SIZE);
          const parts = [];
          // Skip only parts whose stored size matches what THIS file would have
          // written there — a size mismatch means a different render.
          const already = new Map();
          if (resume) {
            for (const pt of (resume.parts || [])) {
              const idx = pt.PartNumber - 1;
              if (idx < 0 || idx >= totalParts) continue;
              const expected = Math.min((idx + 1) * CHUNK_SIZE, f.size) - idx * CHUNK_SIZE;
              if (pt.Size === expected) already.set(pt.PartNumber, pt);
            }
            for (const [num, pt] of already) {
              parts.push({ PartNumber: num, ETag: pt.ETag });
              completedBytes += pt.Size;
            }
            setByteProgress({ loaded: completedBytes, total: f.size });
            setProgress(`Resuming — ${already.size}/${totalParts} parts already sent`);
          }
          for (let p = 0; p < totalParts; p++) {
            const start = p * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, f.size);
            const chunk = f.slice(start, end);
            const partNum = p + 1;
            if (already.has(partNum)) continue;     // already in R2 — do not re-send
            setProgress(`Uploading ${i + 1}/${files.length} — part ${partNum}/${totalParts}`);
            // Per-part retry: one transient blip retries THIS part, not the whole file.
            let partOk = false, lastErr = null;
            for (let attempt = 1; attempt <= 3 && !partOk; attempt++) {
              try {
                const { data: partData } = await api.post('/storage/presign-part', {
                  file_id: presign.data.file_id, upload_id: mp.upload_id, part_number: partNum,
                });
                const etag = await new Promise((resolve, reject) => {
                  const xhr = new XMLHttpRequest();
                  xhr.open('PUT', partData.url);
                  let lastUpdate = 0;
                  xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) {
                      const now = performance.now();
                      if (now - lastUpdate > 250) { setByteProgress({ loaded: completedBytes + e.loaded, total: f.size }); lastUpdate = now; }
                    }
                  };
                  xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag'));
                    else reject(new Error(`Part ${partNum} failed (${xhr.status})`));
                  };
                  xhr.onerror = () => reject(new Error(`Part ${partNum} network error`));
                  xhr.send(chunk);
                });
                parts.push({ PartNumber: partNum, ETag: etag });
                completedBytes += (end - start);
                setByteProgress({ loaded: completedBytes, total: f.size });
                partOk = true;
              } catch (e) {
                lastErr = e;
                if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt));
              }
            }
            if (!partOk) throw lastErr || new Error(`Part ${partNum} failed`);
          }
          // Idempotent + retriable completion (server completes from authoritative parts,
          // validated against expected_parts/expected_total_bytes; 409 = incomplete, not transient).
          let completed = false, cErr = null;
          for (let a = 1; a <= 3 && !completed; a++) {
            try {
              await api.post('/storage/complete-multipart', { file_id: presign.data.file_id, upload_id: mp.upload_id, parts, expected_parts: totalParts, expected_total_bytes: f.size });
              completed = true;
            } catch (e) {
              const d = e?.response?.data;
              if (e?.response?.status === 409 && d?.error === 'upload_incomplete') {
                throw new Error(`Upload incomplete — only ${d.uploaded_parts} of ${d.expected_parts} parts reached storage. Please retry.`);
              }
              // The file in storage is not whole (lib/uploadRefusal.js): final, so no second try; its words are shown.
              if (completionRefused(e)) throw e;
              cErr = e; if (a < 3) await new Promise(r => setTimeout(r, 1500 * a));
            }
          }
          currentMp = null;
          if (!completed) throw cErr || new Error('Completion failed');
          setByteProgress({ loaded: 0, total: 0 });
        } else if (presign.data.upload_url === 'LOCAL') {
          // ── EXISTING: LOCAL fallback (server-mediated upload) — UNCHANGED ──
          const formData = new FormData();
          formData.append('file', f);
          await api.post(`/storage/upload/${presign.data.file_id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          // Small-file single PUT to R2. fetch() does NOT throw on 4xx/5xx, so we
          // MUST check res.ok — otherwise a failed PUT (expired URL, R2 error) sails
          // through as success and confirm-upload marks an empty file "completed".
          const putRes = await fetch(presign.data.upload_url, { method: 'PUT', body: f, headers: { 'Content-Type': f.type || 'application/octet-stream' } });
          if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
        }
        await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
        results.push({ id: presign.data.file_id, key: presign.data.key, filename: f.name, size: f.size });
      }
      setUploaded(prev => [...prev, ...results]);
      setFiles([]);
      toast.success(`${results.length} file${results.length > 1 ? 's' : ''} uploaded`);
      if (onUploadComplete) onUploadComplete(results);
    } catch (e) {
      // KEEP THE PARTS so the next attempt resumes instead of re-sending the file
      // from part 1 (see VideoReview for the incident this comes from). Abandoned
      // uploads are reclaimed by the daily sweep on MULTIPART_RESUME_HOLD_DAYS.
      setByteProgress({ loaded: 0, total: 0 });
      // A refusal (not whole, or a video still being exported) is shown in the server's own words.
      const said = uploadRefusalOf(e);
      if (said) toast.error(said, { duration: 15000 });
      else toast.error('Upload failed: ' + (e.message || 'try again'));
    } finally { setUploading(false); setProgress(''); }
  };

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} onClick={onClick}
        className="p-4 rounded-md border-2 border-dashed border-white/[0.1] hover:border-gold-leaf/30 transition-all cursor-pointer text-center group">
        <input ref={inputRef} type="file" multiple={multiple} accept={accept} onChange={onInputChange} className="hidden" />
        <Icon className="w-5 h-5 text-gold-leaf/30 group-hover:text-gold-leaf/50 mx-auto mb-1.5 transition-colors" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-gold-leaf/70 block">{label || 'Upload Files'}</span>
        <span className="text-[10px] text-slate-500 block mt-0.5">{hint || 'Drop files here or click to browse'}</span>
      </div>

      {/* Pending files list */}
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded bg-white/[0.02] border border-white/[0.04]">
              <FileText className="w-3 h-3 text-slate-500 shrink-0" />
              <span className="text-[11px] text-silk-cream flex-1 truncate">{f.name}</span>
              <span className="text-[9px] text-slate-500 font-mono">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
              <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-velvet-burgundy tap-sm"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <Button onClick={handleUpload} disabled={uploading} size="sm"
            className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 font-mono text-[10px] h-8 w-full">
            {uploading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{progress}</> : <><Upload className="w-3 h-3 mr-1" />Upload {files.length} file{files.length > 1 ? 's' : ''}</>}
          </Button>
          {/* Byte-level progress bar — multipart branch only. Hidden during single-PUT
              (small-file path) because byteProgress.total stays at 0 there. */}
          {uploading && byteProgress.total > 0 && (
            <div className="space-y-1 px-0.5" data-testid="upload-progress">
              <div className="flex items-center justify-between font-mono text-[9px] text-slate-400">
                <span data-testid="upload-progress-percent">{((byteProgress.loaded / byteProgress.total) * 100).toFixed(0)}%</span>
                <span data-testid="upload-progress-bytes">
                  {(byteProgress.loaded / 1024 / 1024).toFixed(0)} MB of {(byteProgress.total / 1024 / 1024).toFixed(0)} MB
                </span>
              </div>
              <div className="h-1 rounded bg-white/[0.05] overflow-hidden">
                <div
                  className="h-full bg-gold-leaf transition-[width] duration-200 ease-out"
                  style={{ width: `${Math.min(100, (byteProgress.loaded / byteProgress.total) * 100)}%` }}
                  data-testid="upload-progress-fill"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Already uploaded files */}
      {uploaded.length > 0 && (
        <div className="space-y-1">
          {uploaded.map((f, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded bg-oxidized-teal/[0.04] border border-oxidized-teal/10">
              <Check className="w-3 h-3 text-oxidized-teal shrink-0" />
              <span className="text-[11px] text-silk-cream/70 flex-1 truncate">{f.filename}</span>
              <span className="text-[9px] text-slate-500 font-mono">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
